/**
 * StatusPopover — wraps a cell value (status badge, timestamp, battery icon…) and
 * shows a one-line detail on hover. Renders `children` bare when there is no
 * `message`, so it can wrap a value unconditionally.
 *
 * For an explanatory note behind an ⓘ use InfoPopover; for the reason a control
 * is disabled use BlockedReasonTip (click-to-pin, not-allowed cursor). This one is
 * the plain informational hover the device, server and resident tables use.
 *
 * Seven tables used to carry their own copy of this. Four of them returned early
 * above the effects, which is why it lives here now — see the note on the bail-out.
 *
 * Styling: `.na-type-popover`, defined in pages/events/alerts/alerts-page.css
 * (available app-wide because every page stylesheet ships in one bundle).
 */

import { useEffect, useRef, useState } from 'react';

const POPOVER_W = 260;

const StatusPopover = ({ children, message }) => {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState({});
  const ref = useRef(null);
  const hoverTimeout = useRef(null);

  const handleEnter = () => {
    clearTimeout(hoverTimeout.current);
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const centerX = rect.left + rect.width / 2;
    const left = Math.max(8, Math.min(centerX - POPOVER_W / 2, window.innerWidth - POPOVER_W - 8));

    setStyle({
      position: 'fixed',
      zIndex: 9999,
      left: `${left}px`,
      top: spaceBelow < 100 ? undefined : `${rect.bottom + 6}px`,
      bottom: spaceBelow < 100 ? `${window.innerHeight - rect.top + 6}px` : undefined,
      width: `${POPOVER_W}px`,
      whiteSpace: 'normal',
    });
    setOpen(true);
  };

  const handleLeave = () => {
    hoverTimeout.current = setTimeout(() => setOpen(false), 100);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  useEffect(() => () => clearTimeout(hoverTimeout.current), []);

  /* Bail out AFTER every hook has run, never before. `message` changes at runtime
     — a device starts alarming, a status goes stale — and an early return above
     the effects makes the hook count change with it, which React rejects with
     "Rendered more/fewer hooks than expected" and takes the whole tab down. */
  if (!message) return children;

  return (
    <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={handleLeave} style={{ position: 'relative', display: 'inline-block' }}>
      {children}
      {open ? (
        <span className="na-type-popover" style={style}>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>{message}</span>
        </span>
      ) : null}
    </span>
  );
};

export default StatusPopover;
